// Planches contact (outils de développement, aucune donnée de jeu) :
//   /?sheet=classes             les 180 classes (et le petit tank) côte à côte, style Atelier ;
//   /?sheet=variants&family=C01 une famille : 12 classes × 5 styles = 60 des 900 variantes ;
//   /?sheet=modules             niveau 250 : les 6 modules visibles, puis les 30 classes Maître et Légende.
import { useEffect, useRef } from 'react';
import { Application, Container, Graphics, Text } from 'pixi.js';
import { CLASSES, type ClassNode } from '../../../shared/catalog/classes';
import { STYLES, STYLE_CODE, STYLE_DEFS } from '../../../shared/catalog/variants';
import { ACCENT_SELF, ART_R } from '../game/render/art';
import { MOD_BIT, drawModules, hexRing } from '../game/render/moduleArt';
import { MODULES } from '../../../shared/catalog/modules';
import { drawTankParts } from '../game/render/variants';

function tank(c: ClassNode, code: number, x: number, y: number, size: number, modules = 0): Container {
  const root = new Container();
  const parts = { hull: new Graphics(), turret: new Graphics(), auto: new Graphics() };
  drawTankParts(parts, c, code, ACCENT_SELF);
  parts.hull.rotation = -Math.PI / 2;
  parts.turret.rotation = -Math.PI / 4;
  parts.auto.rotation = Math.PI / 3;
  const mods = { modHull: new Graphics(), modTurret: new Graphics(), modRadar: new Graphics() };
  drawModules(mods, modules);
  mods.modHull.rotation = mods.modRadar.rotation = -Math.PI / 2;
  mods.modTurret.rotation = Math.PI / 4;
  // Flamme des propulseurs allumée sur la planche.
  if (mods.modHull.children[0] && modules & MOD_BIT.thrusters) mods.modHull.children[0].alpha = 1;
  const shield = new Graphics();
  if (modules & MOD_BIT.shield) {
    shield.circle(0, 0, ART_R * 1.42).fill({ color: 0x6fb6ff, alpha: 0.1 }).stroke({ width: 4, color: 0x9cd2ff, alpha: 0.85 });
    shield.poly(hexRing(ART_R * 1.3)).stroke({ width: 1.5, color: 0xd8f0ff, alpha: 0.55 });
  }
  root.addChild(parts.hull, mods.modHull, mods.modTurret, parts.turret, mods.modRadar, parts.auto, shield);
  root.position.set(x, y);
  root.scale.set(size / ART_R);
  return root;
}

function label(text: string, x: number, y: number, size = 11): Text {
  const t = new Text({ text, style: { fontFamily: 'system-ui', fontSize: size, fill: 0xdfe6ee, align: 'center' } });
  t.anchor.set(0.5, 0);
  t.position.set(x, y);
  return t;
}

function useSheet(draw: (app: Application) => void, width: number, height: number) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const app = new Application();
    let destroyed = false;
    void app.init({ width, height, background: 0x151a20, antialias: true, resolution: 1 }).then(() => {
      if (destroyed) return;
      host.appendChild(app.canvas);
      draw(app);
      app.render();
    });
    return () => {
      destroyed = true;
      app.destroy({ removeView: true });
    };
  }, []);
  return ref;
}

export function ClassSheet() {
  const cell = 150;
  const cols = 11;
  const rows = Math.ceil(CLASSES.length / cols);
  const ref = useSheet(
    (app) =>
      CLASSES.forEach((c, i) => {
        const x = (i % cols) * cell + cell / 2;
        const y = Math.floor(i / cols) * cell + cell / 2 - 10;
        app.stage.addChild(tank(c, STYLE_CODE.atelier, x, y, 28), label(`${c.id}\n${c.name}`, x, y + 44));
      }),
    cols * cell,
    rows * cell,
  );
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="class-sheet" />;
}

export function VariantSheet({ family }: { family: string }) {
  const list = CLASSES.filter((c) => c.id.startsWith(`${family}-`));
  const cell = 175;
  const left = 170;
  const top = 46;
  const ref = useSheet(
    (app) => {
      app.stage.addChild(label(`${list[0]?.family ?? family} — ${list.length} classes × ${STYLES.length} styles`, (left + STYLES.length * cell) / 2, 8, 15));
      STYLES.forEach((s, j) => app.stage.addChild(label(STYLE_DEFS[s].name, left + j * cell + cell / 2, 28, 13)));
      list.forEach((c, i) => {
        const y = top + i * cell + cell / 2 - 8;
        const name = label(`${c.id}\n${c.name}\nniv. ${c.level}`, left / 2, y - 22, 12);
        app.stage.addChild(name);
        STYLES.forEach((s, j) => app.stage.addChild(tank(c, STYLE_CODE[s], left + j * cell + cell / 2, y, 46)));
      });
    },
    left + STYLES.length * cell,
    top + list.length * cell,
  );
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="variant-sheet" />;
}

/** Niveau 250 : chaque module seul sur un Titan, puis les 30 classes Maître et Légende avec quatre modules. */
export function ModuleSheet() {
  const cell = 170;
  const cols = 6;
  const extra = CLASSES.filter((c) => c.level >= 100);
  const titan = CLASSES.find((c) => c.id === 'C01-L')!;
  const rows = 1 + Math.ceil(extra.length / cols);
  const ref = useSheet(
    (app) => {
      MODULES.forEach((m, i) => {
        const x = i * cell + cell / 2;
        app.stage.addChild(tank(titan, STYLE_CODE.atelier, x, cell / 2 - 10, 34, m.bit), label(m.name, x, cell / 2 + 46));
      });
      extra.forEach((c, i) => {
        const x = (i % cols) * cell + cell / 2;
        const y = (1 + Math.floor(i / cols)) * cell + cell / 2 - 10;
        // Quatre modules différents selon la classe (un module en moins, tour à tour).
        const mask = 63 & ~MODULES[i % 6].bit & ~MODULES[(i + 3) % 6].bit;
        app.stage.addChild(tank(c, STYLE_CODE.atelier, x, y, 34, mask), label(`${c.id} · niv. ${c.level}\n${c.name}`, x, y + 48));
      });
    },
    cols * cell,
    rows * cell,
  );
  return <div ref={ref} style={{ overflow: 'auto', height: '100%' }} data-testid="module-sheet" />;
}
